import React, { useState, useEffect, useRef } from "react";
import Constants from "../Variables/Constants";
import COUNTRIES from "../Variables/countries";
import axios from "axios";
import { toast } from "react-toastify";
import color1 from "../../assets/images/dummy/color1.png";
import { Link, useNavigate } from "react-router-dom";
import { Stage, Layer, Image as KonvaImage } from "react-konva";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { setCartItems } from "../../redux/cartSlice"; // Import the action
import Loader from "../../components/Loader";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "./CheckoutPage.css";

import Navigation from "../../components/Navigation";
import { Button } from "react-bootstrap";

const NY_TAX_RATE = 0.08875;

const UPS_US_DOMESTIC_SERVICE_CODES = new Set([
  "01",
  "02",
  "03",
  "07",
  "08",
  "11",
  "12",
  "13",
  "14",
  "59",
]);

const UPS_INTERNATIONAL_SERVICE_CODES = new Set(["07", "08", "11"]);

const isNewYorkState = (value) => {
  if (typeof value !== "string") return false;
  const normalizedValue = value.trim().toLowerCase();
  return normalizedValue === "new york" || normalizedValue === "ny";
};

const COUNTRY_MAP = Object.fromEntries(COUNTRIES.map((country) => [country.code, country]));
const COUNTRY_CODE_MAP = COUNTRIES.reduce((acc, country) => {
  const code = String(country.code || "").toUpperCase();
  const name = String(country.name || "").toLowerCase();
  if (code) acc[code.toLowerCase()] = code;
  if (name) acc[name] = code;
  return acc;
}, {});

const STATE_CODE_MAP = {
  ny: "NY",
  "new york": "NY",
  mh: "MH",
  maharashtra: "MH",
  "ca": "CA",
  "california": "CA",

};

const normalizeCountryCode = (value) => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  // If already a 2-letter uppercase code (from the new dropdown), return as-is
  if (/^[A-Z]{2}$/.test(trimmed)) return trimmed;
  const normalized = trimmed.toLowerCase();
  return COUNTRY_CODE_MAP[normalized] || trimmed.toUpperCase();
};

const normalizeStateCode = (value, countryCode = "") => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";

  const normalizedCountryCode = normalizeCountryCode(countryCode);
  const states = COUNTRY_MAP[normalizedCountryCode]?.states || [];
  const matchedState = states.find(
    (state) =>
      state.code.toLowerCase() === trimmed.toLowerCase() ||
      state.name.toLowerCase() === trimmed.toLowerCase()
  );
  if (matchedState) return matchedState.code;

  // If already a known code format (2-3 uppercase letters), return as-is
  if (/^[A-Z]{2,3}$/.test(trimmed)) return trimmed;
  const normalized = trimmed.toLowerCase();
  return STATE_CODE_MAP[normalized] || trimmed.toUpperCase();
};

const normalizeAddressForForm = (address = {}) => {
  const countryCode = normalizeCountryCode(address.country);
  return {
    ...address,
    country: countryCode,
    state: normalizeStateCode(address.state, countryCode),
  };
};

const getCountryNameByCode = (countryCode = "") => {
  const normalizedCountryCode = normalizeCountryCode(countryCode);
  return COUNTRY_MAP[normalizedCountryCode]?.name || String(countryCode || "").trim();
};

const getStateNameByCode = (countryCode = "", stateCode = "") => {
  const normalizedCountryCode = normalizeCountryCode(countryCode);
  const normalizedStateCode = normalizeStateCode(stateCode, normalizedCountryCode);
  const matchedState = (COUNTRY_MAP[normalizedCountryCode]?.states || []).find(
    (state) => state.code === normalizedStateCode
  );

  return matchedState?.name || String(stateCode || "").trim();
};

const buildCheckoutDeliveryAddress = (addressPayload = {}, fallbackPhone = "") => {
  const countryCode = normalizeCountryCode(addressPayload?.country);
  const stateCode = normalizeStateCode(addressPayload?.state, countryCode);

  return {
    ...addressPayload,
    country: getCountryNameByCode(countryCode),
    country_code: countryCode,
    countryCode,
    state: getStateNameByCode(countryCode, stateCode),
    state_code: stateCode,
    stateCode,
    phone: String(addressPayload?.phone || "").trim() || String(fallbackPhone || "").trim(),
  };
};

const buildValidateAddressPayload = (addressPayload) => ({
  address: String(addressPayload?.address || "").trim(),
  city: String(addressPayload?.city || "").trim(),
  state: normalizeStateCode(addressPayload?.state, addressPayload?.country),
  countryCode: normalizeCountryCode(addressPayload?.country),
  postalCode: String(addressPayload?.postal_code || "").trim(),
});

const buildShipmentMetricsFromCartItems = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) return null;

  let totalWeight = 0;
  let maxLength = 0;
  let maxWidth = 0;
  let maxHeight = 0;
  let hasWeight = false;
  let hasDimensions = false;

  items.forEach((item) => {
    const quantity = Number(item?.quantity || 1);
    const weight = Number(item?.weight || item?.shipping_weight || 0);
    if (Number.isFinite(weight) && weight > 0) {
      totalWeight += weight * quantity;
      hasWeight = true;
    }

    const dimensions =
      item?.dimensions ||
      item?.size_id?.dimensions ||
      item?.customized_image_id?.size_id?.dimensions ||
      item?.product_size?.dimensions ||
      null;

    if (dimensions) {
      const length = Number(dimensions?.length || dimensions?.height || 0);
      const width = Number(dimensions?.width || 0);
      const height = Number(dimensions?.height || dimensions?.depth || 0);

      if (Number.isFinite(length) && length > 0) {
        maxLength = Math.max(maxLength, length);
        hasDimensions = true;
      }
      if (Number.isFinite(width) && width > 0) {
        maxWidth = Math.max(maxWidth, width);
        hasDimensions = true;
      }
      if (Number.isFinite(height) && height > 0) {
        maxHeight = Math.max(maxHeight, height);
        hasDimensions = true;
      }
    }
  });

  if (!hasWeight && !hasDimensions) return null;

  return {
    weight: Number(totalWeight.toFixed(2)) || 0,
    weightUnit: "LBS",
    length: Number(maxLength.toFixed(2)) || 0,
    width: Number(maxWidth.toFixed(2)) || 0,
    height: Number(maxHeight.toFixed(2)) || 0,
    dimensionUnit: "IN",
  };
};

const hasMinimumAddressForRating = (addressPayload = {}) => {
  const payload = buildValidateAddressPayload(addressPayload);
  return Boolean(
    payload.address &&
      payload.city &&
      payload.state &&
      payload.countryCode &&
      payload.postalCode
  );
};



const filterServiceOptionsByCountry = (serviceOptions = [], countryCode = "") => {
  const normalizedCountryCode = String(countryCode || "").trim().toUpperCase();
  if (!Array.isArray(serviceOptions) || serviceOptions.length === 0) return [];

  const allowedCodes =
    normalizedCountryCode === "US"
      ? UPS_US_DOMESTIC_SERVICE_CODES
      : UPS_INTERNATIONAL_SERVICE_CODES;

  const filtered = serviceOptions.filter((option) =>
    allowedCodes.has(String(option?.serviceCode || "").trim())
  );

  // Keep UPS-returned options if filtering leaves nothing for a destination.
  return filtered.length > 0 ? filtered : serviceOptions;
};

const resolvePreferredServiceOption = ({
  serviceOptions = [],
  preferredServiceCode = "",
  countryCode = "",
}) => {
  if (!Array.isArray(serviceOptions) || serviceOptions.length === 0) return null;

  const normalizedPreferredCode = String(preferredServiceCode || "").trim();
  if (normalizedPreferredCode) {
    const matchedPreferred = serviceOptions.find(
      (option) => option.serviceCode === normalizedPreferredCode
    );
    if (matchedPreferred) return matchedPreferred;
  }

  const normalizedCountryCode = String(countryCode || "").trim().toUpperCase();
  if (normalizedCountryCode === "US") {
    const upsGround = serviceOptions.find((option) => option.serviceCode === "03");
    if (upsGround) return upsGround;
  }

  return serviceOptions[0] || null;
};

const formatUpsServiceLabel = ({ serviceCode = "", serviceDescription = "" }) => {
  const code = String(serviceCode || "").trim();
  const description = String(serviceDescription || "").trim();

  if (description && code) return `${description} (${code})`;
  if (description) return description;
  if (code) return code;
  return "-";
};

const enrichServiceOptionsWithFallback = (serviceOptions = [], fallbackOptions = []) => {
  const fallbackDescriptionByCode = new Map(
    (fallbackOptions || []).map((option) => [
      String(option?.serviceCode || "").trim(),
      String(option?.serviceDescription || "").trim(),
    ])
  );

  return (serviceOptions || []).map((option) => {
    const serviceCode = String(option?.serviceCode || "").trim();
    const serviceDescription = String(option?.serviceDescription || "").trim();

    return {
      ...option,
      serviceCode,
      serviceDescription: serviceDescription || fallbackDescriptionByCode.get(serviceCode) || "",
    };
  });
};

const extractShippingCharge = (responseData) => {
  if (!responseData || typeof responseData !== "object") return 0;

  const firstRate = Array.isArray(responseData?.rates) ? responseData.rates[0] : null;
  const firstServiceRate = Array.isArray(responseData?.serviceRates)
    ? responseData.serviceRates[0]
    : null;

  const possibleValues = [
    responseData?.shipping_charges,
    responseData?.shippingCharges,
    responseData?.shippingCharge,
    responseData?.amount,
    responseData?.rate,
    responseData?.data?.shipping_charges,
    responseData?.data?.shippingCharges,
    responseData?.data?.shippingCharge,
    responseData?.data?.amount,
    responseData?.data?.rate,
    firstRate?.amount,
    firstRate?.total,
    firstRate?.rate,
    firstServiceRate?.amount,
    firstServiceRate?.total,
    firstServiceRate?.rate,
  ];

  const parsed = possibleValues
    .map((value) => Number(value))
    .find((value) => Number.isFinite(value) && value >= 0);

  return Number.isFinite(parsed) ? parsed : 0;
};

const extractServiceOptions = (responseData) => {
  const source =
    responseData?.serviceOptions ||
    responseData?.data?.serviceOptions ||
    responseData?.rates ||
    responseData?.data?.rates ||
    [];

  if (!Array.isArray(source)) return [];

  const mapped = source
    .map((item) => {
      const serviceCode = String(item?.serviceCode || item?.code || "").trim();
      const serviceDescription = String(
        item?.serviceDescription || item?.description || ""
      ).trim();
      const amount = Number(
        item?.amount ?? item?.rate ?? item?.total ?? item?.shippingCharges ?? 0
      );

      if (!serviceCode || !Number.isFinite(amount) || amount < 0) {
        return null;
      }

      return {
        serviceCode,
        serviceDescription,
        amount: Number(amount.toFixed(2)),
      };
    })
    .filter(Boolean);

  return mapped.sort((a, b) => a.amount - b.amount);
};

const isAddressValidationResponseValid = (responseData) => {
  if (!responseData || typeof responseData !== "object") return false;

  if (typeof responseData?.isValid === "boolean") return responseData.isValid;
  if (typeof responseData?.valid === "boolean") return responseData.valid;
  if (typeof responseData?.addressValid === "boolean") return responseData.addressValid;

  const status = String(responseData?.status || responseData?.data?.status || "").toLowerCase();
  const message = String(responseData?.message || responseData?.data?.message || "").toLowerCase();

  if (status === "success" || status === "ok") {
    return !message.includes("invalid");
  }

  return false;
};

export const CustomImage = ({ imageUrl }) => {
  const [image, setImage] = useState(null);
  useEffect(() => {
    const img = new window.Image();
    img.src = imageUrl;
    img.onload = () => setImage(img);
  }, [imageUrl]);
  return image ? (
    <KonvaImage image={image} x={0} y={0} width={100} height={100} />
  ) : null;
};

const addressInitialObj = JSON.parse(
  JSON.stringify({
    address_name: "",
    country: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
    phone: "",
  })
);

const checkoutSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 58,
    borderRadius: 16,
    borderColor: state.isFocused ? "#86b7fe" : "#dee2e6",
    boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13,110,253,.25)" : "none",
    fontSize: 16,
    backgroundColor: state.isDisabled ? "#e9ecef" : "#fff",
    cursor: state.isDisabled ? "not-allowed" : "default",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "10px 14px",
  }),
  input: (provided) => ({
    ...provided,
    fontSize: 16,
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: 16,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#6c757d",
    fontSize: 16,
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 40,
  }),
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState("");
  const token = localStorage.getItem("token");
  const dispatch = useDispatch();
  const [address, setAddress] = useState(addressInitialObj);
  const [addressList, setAddressList] = useState([]);
  const [recipientType, setRecipientType] = useState("self");
  const [recipientDetails, setRecipientDetails] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [shipping_charges, setShippingCharges] = useState(0);
  const [cartData, setCartData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [paymentOption, setPaymentOption] = useState("full_payment");

  let totalShippingCharges = Number(shipping_charges || 0);

  const cartItemsShippingTotal = cartData.reduce((sum, product) => {
    const itemShipping = Number(product?.shipping_charges ?? product?.shippingCharges ?? 0);
    return sum + (Number.isFinite(itemShipping) ? itemShipping : 0);
  }, 0);
  const effectiveShippingAmount =
    cartItemsShippingTotal > 0 ? cartItemsShippingTotal : Number(shipping_charges || 0);
  const displayShippingAmount = Number.isFinite(Number(shipping_charges))
    ? Number(shipping_charges)
    : effectiveShippingAmount;
  totalShippingCharges = effectiveShippingAmount;


  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [isAddressValidated, setIsAddressValidated] = useState(false);
  const [isValidatingAddress, setIsValidatingAddress] = useState(false);
  const [shippingServiceOptions, setShippingServiceOptions] = useState([]);
  const [isServiceCodesLoading, setIsServiceCodesLoading] = useState(false);
    // Fetch UPS service codes when country changes or address is ready
    // Service code options from API, kept separate from rate options
    const [serviceCodeOptions, setServiceCodeOptions] = useState([]);
    const serviceCodeOptionsRef = useRef([]);
    useEffect(() => {
      serviceCodeOptionsRef.current = serviceCodeOptions;
    }, [serviceCodeOptions]);
    useEffect(() => {
      const countryCode = normalizeCountryCode(address?.country);
      if (!countryCode) {
        setServiceCodeOptions([]);
        setShippingServiceOptions([]);
        return;
      }
      if (!token) return;

      const serviceCodeUrl =
        Constants.getUrls.upsServiceCode ||
        `${Constants.getUrls.upsConfig.replace("/config", "/service-code")}`;

      setIsServiceCodesLoading(true);
      axios
        .get(`${serviceCodeUrl}?destinationCountryCode=${countryCode}`, {
          headers: { Authorization: token },
        })
        .then((res) => {
          if (res.data?.status === 'success' && Array.isArray(res.data.data)) {
            const options = res.data.data.map((s) => ({
              serviceCode: s.code,
              serviceDescription: s.description,
              amount: undefined
            }));
            setServiceCodeOptions(options);
            setShippingServiceOptions(options);
          } else {
            setServiceCodeOptions([]);
            setShippingServiceOptions([]);
          }
        })
        .catch(() => {
          setServiceCodeOptions([]);
          setShippingServiceOptions([]);
        })
        .finally(() => setIsServiceCodesLoading(false));
    }, [address?.country, token]);
  const [selectedShippingServiceCode, setSelectedShippingServiceCode] = useState("");
  const [selectedShippingServiceName, setSelectedShippingServiceName] = useState("");

  const countryOptions = COUNTRIES.map((country) => ({
    value: country.code,
    label: `${country.name} (${country.code})`,
  }));
  const selectedCountryStates = COUNTRY_MAP[address?.country]?.states || [];
  const stateOptions = selectedCountryStates.map((state) => ({
    value: state.code,
    label: `${state.name} (${state.code})`,
  }));

  useEffect(() => {
    if (!selectedShippingServiceCode || selectedShippingServiceName) return;

    const matchedService =
      shippingServiceOptions.find((item) => item.serviceCode === selectedShippingServiceCode) ||
      serviceCodeOptionsRef.current.find((item) => item.serviceCode === selectedShippingServiceCode);

    if (matchedService?.serviceDescription) {
      setSelectedShippingServiceName(matchedService.serviceDescription);
    }
  }, [selectedShippingServiceCode, selectedShippingServiceName, shippingServiceOptions]);

  console.log("cartData", cartData);
  console.log("address", address);
  console.log("addressList", addressList);
  console.log(userData, "user");
  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${Constants.getUrls.getUserAddress}`, {
        headers: { Authorization: token },
      });

      if (response.data.status === "success") {
        const data = Array.isArray(response.data.data)
          ? response.data.data.map((item) => normalizeAddressForForm(item))
          : [];
        if (data && Array.isArray(data) && data.length > 0) {
          setAddressList(data);
          setAddress(
            data.find((item) => item.is_default === "yes")
              ? data.find((item) => item.is_default === "yes")
              : addressInitialObj
          );
        } else {
          setAddress(addressInitialObj);
        }
      } else {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error(response.data.message);
        setAddress(addressInitialObj);
      }
    } catch (error) {
      try {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error(error.message);
        const { status, message } = error.response.data;
        if (status === 401 && message === "Unauthorized!") {
          localStorage.removeItem("token");
          window.location.href = "/";
        }
      } catch (error) {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error(error.message);
      }
    }
  };

  useEffect(() => {
    if (cartData) {
      if (Array.isArray(cartData)) {
        let sum = 0;
        cartData.map((item) => {
          if (item?.custom_product === "yes") {
            sum = sum + item?.customized_image_id?.price * item?.quantity;
          } else {
            sum = sum + item?.price * item?.quantity;
          }
        });
        setTotalAmount(sum);
      }
    }
  }, [cartData]);

  useEffect(() => {
    if (recipientType !== "self") return;

    const defaultAddress =
      addressList.find((item) => item.is_default === "yes") ||
      addressList[0] ||
      addressInitialObj;

    setRecipientDetails({
      name: userData?.name || "",
      email: userData?.email || "",
      phone: userData?.mobile_no ? String(userData.mobile_no) : "",
    });
    setAddress(normalizeAddressForForm(defaultAddress));
  }, [recipientType, userData, addressList]);

  useEffect(() => {
    if (recipientType !== "other") return;

    setRecipientDetails({
      name: "",
      email: "",
      phone: "",
    });
    setAddress(addressInitialObj);
    setShippingCharges(0);
    setIsAddressValidated(false);
    setShippingServiceOptions([]);
    setSelectedShippingServiceCode("");
    setSelectedShippingServiceName("");
  }, [recipientType]);

  useEffect(() => {
    if (recipientType !== "self") return;
    if (!address?._id) return;

    validateAddressAndFetchRates(address);
  }, [recipientType, address?._id]);

  useEffect(() => {
    if (recipientType !== "other") return;

    if (!hasMinimumAddressForRating(address)) {
      setShippingCharges(0);
      setIsAddressValidated(false);
      setShippingServiceOptions([]);
      setSelectedShippingServiceCode("");
      setSelectedShippingServiceName("");
      return;
    }

    const timeoutId = setTimeout(() => {
      validateAddressAndFetchRates(address);
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [
    recipientType,
    address?.address,
    address?.city,
    address?.state,
    address?.country,
    address?.postal_code,
  ]);

  useEffect(() => {
    const discountPercent = Math.min(
      Math.max(Number(userData?.discountAmount || 0), 0),
      100
    );
    const discountAmount = Number(
      Math.min(
        (Math.max(Number(totalAmount || 0), 0) * discountPercent) / 100,
        Math.max(Number(totalAmount || 0), 0)
      ).toFixed(2)
    );
    const taxableSubtotal = Number(Math.max(totalAmount - discountAmount, 0).toFixed(2));

    const shouldApplyTax =
      userData?.isTaxExempted === false && isNewYorkState(address?.state);

    if (!shouldApplyTax) {
      setTaxAmount(0);
      return;
    }

    const calculatedTax = Number((taxableSubtotal * NY_TAX_RATE).toFixed(2));
    setTaxAmount(calculatedTax);
  }, [address, totalAmount, userData?.isTaxExempted, userData?.discountAmount]);

  const fetchCart = async () => {
    try {
      const res = await axios.get(Constants.getUrls.getCart, {
        headers: { Authorization: token },
      });
      if (res.data.status === "success") {
        const data = res.data.data.docs;
        const arr = [];
        if (data && Array.isArray(data) && data.length > 0) {
          data.map((item) => {
            arr.push({
              ...item,
              product_id: item.product_id._id,
              images: item.product_id.images,
              name: item.product_id.name,
              price: item.product_id.price,
              matching_bag: item.product_id?.matching_bag || "",
              price_data: item.product_id?.price_data || "",
              design_data: item.product_id?.design_data || "",
            });
          });
          localStorage.removeItem("cart");
          setCartData(arr);
          dispatch(setCartItems(arr));
        } else {
          setCartData([]);
          dispatch(setCartItems([]));
          localStorage.removeItem("cart");
        }
      } else {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error("Failed to fetch cart items.");
        setCartData([]);
        dispatch(setCartItems([]));
        localStorage.removeItem("cart");
      }
    } catch (err) {
      console.error(err);
      toast.clearWaitingQueue();
      toast.dismiss();
      toast.error("Failed to fetch cart items.");
    }
  };

  const fetchUserData = async () => {
    const url = Constants.getUrls.getUserData;
    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: token,
        },
      });
      if (response.data.status === "success") {
        setUserData(response.data.data);
      } else {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error(response.data.message);
      }
    } catch (error) {
      try {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error(error.message);
        const { status, message } = error.response.data;
        if (status === 401 && message === "Unauthorized!") {
          localStorage.removeItem("token");
          window.location.href = "/";
        }
      } catch (error) {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error(error.message);
      }
    }
  };

  useEffect(() => {
    if (token) {
      fetchUserData();
      fetchAddresses();
      fetchCart();
    } else {
      navigate("/login", {
        state: { locationPath: window.location.href, data: null },
      });
      toast.clearWaitingQueue();
      toast.dismiss();
      toast.error("Please login to continue.");
    }
  }, [token]);

  // const handleRemoveFromCart = async (ids) => {
  //   try {
  //     const resp = await axios.delete(
  //       `${Constants.deleteUrls.removeCartItem}`,
  //       {
  //         headers: { Authorization: token },
  //         data: { ids },
  //       }
  //     );
  //     if (resp.data.status === "success") {
  //       toast.clearWaitingQueue();
  //       toast.dismiss();
  //       toast.success("Item removed from cart!", {
  //         style: { backgroundColor: "green", color: "white" },
  //       });
  //       fetchCart();
  //     } else {
  //       toast.clearWaitingQueue();
  //       toast.dismiss();
  //       toast.error("Failed to remove item from cart.");
  //     }
  //   } catch (err) {
  //     console.error(err);
  //     toast.clearWaitingQueue();
  //     toast.dismiss();
  //     toast.error("Failed to remove item from cart.");
  //   }
  // };

  // after select or add new address call validate address and rate api to check address is valid and get shipping rates, if address is invalid then show error and do not allow to place order, if address is valid then allow to place order and also show shipping rates in order summary section

  const validateAddressAndFetchRates = async (addressPayload, preferredServiceCode = "") => {
    const payload = buildValidateAddressPayload(addressPayload);
    const fallbackServiceOptions = serviceCodeOptionsRef.current;

    if (!hasMinimumAddressForRating(addressPayload)) {
      setShippingCharges(0);
      setIsAddressValidated(false);
      setShippingServiceOptions(fallbackServiceOptions); // fallback to static options
      setSelectedShippingServiceCode("");
      setSelectedShippingServiceName("");
      return null;
    }

    setIsValidatingAddress(true);
    try {
      const serviceCodeToUse =
        String(
          preferredServiceCode ||
            selectedShippingServiceCode ||
            (payload.countryCode === "US" ? "03" : "")
        ).trim();

      const response = await axios.post(
        `${Constants.postUrls.validateAddressAndFetchRates}`,
        {
          deliveryAddress: payload,
          serviceCode: serviceCodeToUse,
          cartItems: cartData || [],
          shipmentMetrics: buildShipmentMetricsFromCartItems(cartData),
        },
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        }
      );

      const data = response?.data;
      const isValidAddress = isAddressValidationResponseValid(data);
      const resolvedShipping = extractShippingCharge(data);

      if (!isValidAddress) {
        setShippingCharges(0);
        setIsAddressValidated(false);
        setShippingServiceOptions(fallbackServiceOptions); // fallback to static options
        setSelectedShippingServiceCode("");
        setSelectedShippingServiceName("");
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error(data?.message || "Invalid delivery address. Please check and try again.");
        return null;
      }

      const serviceOptions = extractServiceOptions(data);
      const filteredServiceOptions = filterServiceOptionsByCountry(
        serviceOptions,
        payload.countryCode
      );
      const enrichedServiceOptions = enrichServiceOptionsWithFallback(
        filteredServiceOptions,
        fallbackServiceOptions
      );
      let selectedOption = resolvePreferredServiceOption({
        serviceOptions: enrichedServiceOptions,
        preferredServiceCode: String(
          data?.selectedServiceCode || serviceCodeToUse || ""
        ).trim(),
        countryCode: payload.countryCode,
      });

      // If no valid rates, fallback to static options and select first
      if (!selectedOption && fallbackServiceOptions.length > 0) {
        selectedOption = fallbackServiceOptions[0];
        setShippingServiceOptions(fallbackServiceOptions);
      } else if (enrichedServiceOptions.length > 0) {
        setShippingServiceOptions(enrichedServiceOptions);
      } else {
        setShippingServiceOptions(fallbackServiceOptions);
      }

      setSelectedShippingServiceCode(selectedOption?.serviceCode || "");
      setSelectedShippingServiceName(selectedOption?.serviceDescription || "");

      const responseShippingCharges = Number.isFinite(Number(data?.cartItemsShippingTotal))
        ? Number(data?.cartItemsShippingTotal)
        : Number.isFinite(Number(data?.shippingCharges))
        ? Number(data?.shippingCharges)
        : selectedOption?.amount ?? resolvedShipping;

      setShippingCharges(responseShippingCharges);
      setIsAddressValidated(true);
      return data;
    } catch (error) {
      console.error(error);
      setShippingCharges(0);
      setIsAddressValidated(false);
      setShippingServiceOptions(fallbackServiceOptions);
      setSelectedShippingServiceCode("");
      setSelectedShippingServiceName("");
      toast.clearWaitingQueue();
      toast.dismiss();
      toast.error("Failed to validate address and fetch rates.");
      return null;
    } finally {
      setIsValidatingAddress(false);
    }
  };



  const handleAddorUpdateAddress = async (addressPayload) => {
    await axios.post(
      `${Constants.postUrls.addAddress}`,
      { ...addressPayload, is_default: "yes" },
      {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      }
    );
  };

  const handleCheckout = async () => {
    if (isPlacingOrder) return;
    try {
      const isOtherRecipient = recipientType === "other";

      const isAddressValid =
        address &&
        ["address_name", "country", "address", "city", "postal_code"].every(
          (key) => String(address?.[key] || "").trim() !== ""
        );

      const isRecipientValid =
        String(recipientDetails?.name || "").trim() !== "" &&
        String(recipientDetails?.email || "").trim() !== "" &&
        (recipientType === "self" ||
          String(recipientDetails?.phone || "").trim() !== "");

      if (!isAddressValid) {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error("Please fill all the address fields.");
        return;
      }

      if (!isRecipientValid) {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error(
          recipientType === "other"
            ? "Please fill name, email and phone."
            : "Please fill name and email."
        );
        return;
      }

      const deliveryAddress = buildCheckoutDeliveryAddress(
        address,
        recipientDetails?.phone
      );

      if (isOtherRecipient && String(deliveryAddress.phone || "").trim() === "") {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error("Please add phone number for delivery.");
        return;
      }

      if (!cartData || !Array.isArray(cartData) || cartData.length === 0) {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error("Cart is empty.");
        return;
      }

      if (!selectedShippingServiceCode) {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error("Please select a UPS shipping method.");
        return;
      }

      setIsPlacingOrder(true);
      if (address) {
        await handleAddorUpdateAddress(deliveryAddress);

        const validationResult = await validateAddressAndFetchRates(
          deliveryAddress,
          selectedShippingServiceCode
        );
        if (!validationResult) {
          setIsPlacingOrder(false);
          return;
        }

        const validatedShippingAmount = Number.isFinite(
          Number(validationResult?.cartItemsShippingTotal)
        )
          ? Number(validationResult.cartItemsShippingTotal)
          : Number.isFinite(Number(validationResult?.shippingCharges))
          ? Number(validationResult.shippingCharges)
          : Number(effectiveShippingAmount || 0);

        const arr = cartData.map((item) => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          product_id: item.product_id,
          custom_product: item.custom_product || "no",
          customized_image_id:
            item?.customized_image_id?._id || item?.customized_image_id || null,
          images: item.images,
          matching_bag: item.matching_bag,
          price_data: item.price_data,
        }));
        const checkoutUrl =
          paymentOption === "pay_later"
            ? Constants.postUrls.createDirectOrder
            : Constants.postUrls.createCheckout;

        console.log("[checkout] request", {
          checkoutUrl,
          paymentOption,
          recipientType,
          recipientDetails,
          deliveryAddress,
          itemsCount: arr.length,
        });

        const discountPercent = Math.min(
          Math.max(Number(userData?.discountAmount || 0), 0),
          100
        );
        const discountAmount = Number(
          Math.min(
            (Math.max(Number(totalAmount || 0), 0) * discountPercent) / 100,
            Math.max(Number(totalAmount || 0), 0)
          ).toFixed(2)
        );

        const response = await axios.post(
          `${checkoutUrl}`,
          {
            cartItems: arr,
            recipientType,
            recipientDetails,
            deliveryAddress,
            paymentOption,
            discountPercent,
            discountAmount,
            shippingCharges: validatedShippingAmount,
            shippingServiceCode: selectedShippingServiceCode,
            shippingServiceName: selectedShippingServiceName,
          },
          {
            headers: {
              Authorization: token,
            },
          }
        );
        console.log("[checkout] response", response?.data);
        // Stripe checkout page par redirect
        if (response?.data?.url) {
          console.log("[checkout] redirecting to payment page", response.data.url);
          window.location.href = response.data.url;
        } else if (response?.data?.success) {
          toast.clearWaitingQueue();
          toast.dismiss();
          toast.success(response?.data?.message || "Order placed successfully.");
          fetchCart();
          navigate(
            response?.data?.orderId
              ? `/track?id=${response.data.orderId}`
              : "/orders"
          );
        } else {
          toast.clearWaitingQueue();
          toast.dismiss();
          toast.error("Unable to place order.");
        }
      }
    } catch (error) {
      console.error("[checkout] error", {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      try {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error(error.message);
        const { status, message } = error.response.data;
        if (status === 401 && message === "Unauthorized!") {
          localStorage.removeItem("token");
          window.location.href = "/";
        }
      } catch (error) {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error(error.message);
      }
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const renderPaymentSection = (className = "") => (
    <div className={className}>
      <div className="mb-4 mt-4">
        <h4 className="fs-22 mb-3">Payment Option</h4>
        <div className="d-flex flex-column gap-2 payment-option-group">
          <div className="form-check">
            <input
              className="form-check-input payment-check-input"
              type="radio"
              name="payment-option"
              id="payment-full"
              checked={paymentOption === "full_payment"}
              onChange={() => setPaymentOption("full_payment")}
            />
            <label className="form-check-label h5 mb-0" htmlFor="payment-full">
              Full Payment
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input payment-check-input"
              type="radio"
              name="payment-option"
              id="payment-deposit"
              checked={paymentOption === "deposit_50"}
              onChange={() => setPaymentOption("deposit_50")}
            />
            <label className="form-check-label h5 mb-0" htmlFor="payment-deposit">
              Pay a 50% Deposit
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input payment-check-input"
              type="radio"
              name="payment-option"
              id="payment-later"
              checked={paymentOption === "pay_later"}
              onChange={() => setPaymentOption("pay_later")}
            />
            <label className="form-check-label h5 mb-0" htmlFor="payment-later">
              Pay Later
            </label>
          </div>
        </div>
      </div>

      <Button
        className="bg-warning mt-4 btn-lg w-100 text-white fw-600"
        onClick={handleCheckout}
        variant="warning"
        disabled={
          isPlacingOrder ||
          isValidatingAddress ||
          !isAddressValidated ||
          !selectedShippingServiceCode ||
          !(cartData?.length > 0)
        }
      >
        {isPlacingOrder ? (
          <span className="d-inline-flex align-items-center gap-2">
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            />
            Placing Order...
          </span>
        ) : (
          "Place Order"
        )}
      </Button>
    </div>
  );

  return (
    <div className="container-fluid">
      <Navigation />

      <div className="CheckOut checkout-page container my-5 mx-auto">
        <div className="row checkout-grid justify-content-lg-start justify-content-md-center justify-content-center gy-5">
          {/* Left Section: Contact, Delivery, Payment */}
          <div className="col-12 col-lg-6 col-md-11 col-11 checkout-form-column">
            {/* Contact Section */}
            <div className="mb-4 mt-4">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
                <h4 className="fs-22 mb-0">Contact</h4>
                <div className="d-flex align-items-center gap-4 recipient-toggle">
                <div className="form-check">
                  <input
                    className="form-check-input recipient-check-input"
                    type="radio"
                    name="recipient-type"
                    id="recipient-self"
                    checked={recipientType === "self"}
                    onChange={() => setRecipientType("self")}
                  />
                  <label className="form-check-label h5 mb-0" htmlFor="recipient-self">
                    Self
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input recipient-check-input"
                    type="radio"
                    name="recipient-type"
                    id="recipient-other"
                    checked={recipientType === "other"}
                    onChange={() => setRecipientType("other")}
                  />
                  <label className="form-check-label h5 mb-0" htmlFor="recipient-other">
                    Other
                  </label>
                </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="h5" htmlFor="name">
                    Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Name"
                    id="name"
                    value={recipientDetails?.name}
                    onChange={(e) =>
                      setRecipientDetails({
                        ...recipientDetails,
                        name: e.target.value,
                      })
                    }
                    readOnly={recipientType === "self"}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="h5" htmlFor="email">
                    Email
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    // aria-label="Email Address"
                    id="email"
                    value={recipientDetails?.email}
                    placeholder="Email Address"
                    readOnly={recipientType === "self"}
                    onChange={(e) =>
                      setRecipientDetails({
                        ...recipientDetails,
                        email: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="h5" htmlFor="phone">
                    Phone
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="phone"
                    value={recipientDetails?.phone}
                    placeholder="Phone"
                    readOnly={recipientType === "self"}
                    onChange={(e) =>
                      setRecipientDetails({
                        ...recipientDetails,
                        phone: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Delivery Section */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3 checkout-delivery-header">
                <h4 className="fs-22">Delivery</h4>
                <div className="checkout-address-select-wrapper">
                  <label htmlFor="select-address" className=" fs-13 fw-semibold mb-2">Select Address</label>
                  <select
                    className="form-control w-100 checkout-address-select"
                    name="country"
                    value={address?._id || ""}
                    onChange={(e) => {
                      const val = e.target.value
                      if (val) {
                        const selectedAddress = addressList.find((item) => item._id === val);
                        setAddress(normalizeAddressForForm(selectedAddress));
                      } else {
                        setAddress(addressInitialObj);
                        setShippingCharges(0);
                        setIsAddressValidated(false);
                        setShippingServiceOptions([]);
                        setSelectedShippingServiceCode("");
                        setSelectedShippingServiceName("");
                      }
                    }}
                  >
                    <option value="">Select Address</option>
                    {addressList?.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.address_name}
                      </option>
                    ))}
                  </select>
                </div>
                {/* <Link  to="/address">
                Select Address
              </Link> */}
              </div>
              <div className="mb-3">
                <label className="h5" htmlFor="address-name">
                  Address Name
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Address Name"
                  id="address-name"
                  value={address?.address_name}
                  onChange={(e) =>
                    setAddress({ ...address, address_name: e.target.value })
                  }
                  readOnly={recipientType === "self"}
                />
              </div>
              <div className="mb-3">
                <label className="h5" htmlFor="country">
                  Country
                </label>
                <Select
                  inputId="country"
                  options={countryOptions}
                  value={countryOptions.find((option) => option.value === (address?.country || "")) || null}
                  onChange={(option) => {
                    const countryCode = normalizeCountryCode(option?.value || "");
                    setAddress({ ...address, country: countryCode, state: "" });
                  }}
                  placeholder="Select Country"
                  isSearchable
                  isDisabled={recipientType === "self"}
                  styles={checkoutSelectStyles}
                />
              </div>

              <div className="mb-3">
                <label className="h5" htmlFor="address">
                  Address
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Address"
                  id="address"
                  value={address?.address}
                  onChange={(e) =>
                    setAddress({ ...address, address: e.target.value })
                  }
                  readOnly={recipientType === "self"}
                />
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="h5" htmlFor="city">
                    City
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="City"
                    id="city"
                    value={address?.city}
                    onChange={(e) =>
                      setAddress({ ...address, city: e.target.value })
                    }
                    readOnly={recipientType === "self"}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="h5" htmlFor="postal-code">
                    Zip Code
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Zip Code"
                    id="postal-code"
                    value={address?.postal_code}
                    onChange={(e) =>
                      setAddress({ ...address, postal_code: e.target.value })
                    }
                    readOnly={recipientType === "self"}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="h5" htmlFor="state">
                    State
                  </label>
                  {selectedCountryStates.length > 0 ? (
                    <Select
                      inputId="state"
                      options={stateOptions}
                      value={stateOptions.find((option) => option.value === (address?.state || "")) || null}
                      onChange={(option) =>
                        setAddress({ ...address, state: option?.value || "" })
                      }
                      placeholder="Select State / Province"
                      isSearchable
                      isDisabled={recipientType === "self"}
                      styles={checkoutSelectStyles}
                    />
                  ) : (
                    <input
                      type="text"
                      className="form-control"
                      placeholder="State / Province / Region"
                      id="state"
                      value={address?.state || ""}
                      onChange={(e) =>
                        setAddress({ ...address, state: normalizeStateCode(e.target.value, address?.country) })
                      }
                      readOnly={recipientType === "self"}
                    />
                  )}
                </div>
                {/* <div className="col-md-6 mb-3">
                  <label className="h5" htmlFor="delivery-phone">
                    Delivery Phone
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Delivery Phone"
                    id="delivery-phone"
                    value={address?.phone || ""}
                    onChange={(e) =>
                      setAddress({ ...address, phone: e.target.value })
                    }
                    readOnly={recipientType === "self"}
                  />
                </div> */}
              </div>
              <div className="mb-3">
                <label className="h5" htmlFor="shipping-method">
                  Shipping Method
                </label>
                <select
                  id="shipping-method"
                  className="form-control"
                  value={selectedShippingServiceCode}
                  onChange={async (e) => {
                    const code = e.target.value;
                    setSelectedShippingServiceCode(code);
                    const service = shippingServiceOptions.find(
                      (item) => item.serviceCode === code
                    );
                    const fallbackService = serviceCodeOptions.find(
                      (item) => item.serviceCode === code
                    );
                    setSelectedShippingServiceName(
                      service?.serviceDescription || fallbackService?.serviceDescription || ""
                    );
                    if (code && hasMinimumAddressForRating(address)) {
                      await validateAddressAndFetchRates(address, code);
                    }
                  }}
                  disabled={
                    isServiceCodesLoading ||
                    isValidatingAddress
                  }
                >
                  <option value="">
                    {isServiceCodesLoading
                      ? 'Loading UPS shipping methods...'
                      : shippingServiceOptions.length === 0
                        ? 'No service codes available'
                        : 'Select UPS shipping method'}
                  </option>
                  {shippingServiceOptions.length > 0 && shippingServiceOptions.map((service) => {
                    const fallbackService = serviceCodeOptions.find(
                      (item) => item.serviceCode === service.serviceCode
                    );

                    return (
                      <option key={service.serviceCode} value={service.serviceCode}>
                        {formatUpsServiceLabel({
                          serviceCode: service.serviceCode,
                          serviceDescription:
                            service.serviceDescription || fallbackService?.serviceDescription || "",
                        })}
                      </option>
                    );
                  })}
                </select>
                {selectedShippingServiceName && (
                  <small className="text-muted d-block mt-1">
                    Selected: {formatUpsServiceLabel({
                      serviceCode: selectedShippingServiceCode,
                      serviceDescription: selectedShippingServiceName,
                    })}
                  </small>
                )}
              </div>
              {/* <div className=" d-flex justify-content-start align-items-center fs-14 gap-3 fs-10 ">
              <input type="checkbox" className="checkBox p-2" id="saveInfo" />
              <label
                className="form-check-label text-light-gray"
                htmlFor="saveInfo"
              >
                Save This Info For Future
              </label>
            </div> */}
            </div>

            {/* <h4 className="mb-3 fs-22">Payment</h4>
          <div className="accordion fs-14" id="paymentAccordion">
            <div className="accordion-item w-100">
              <h2 className="accordion-header bg-white" id="headingOne">
                <button
                  className="accordion-button w-100 py-3 d-flex justify-content-between align-items-center"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#collapseOne"
                  aria-expanded="true"
                  aria-controls="collapseOne"
                >
                  <span className="fs-14">Credit Card</span>
                  <div className="float-end d-flex align-items-center justify-content-end w-75">
                    <img loading="lazy" 
                      src="https://upload.wikimedia.org/wikipedia/commons/a/a4/Mastercard_2019_logo.svg"
                      alt="Card Logo"
                      width="30"
                      className="ms-2"
                    />
                  </div>
                </button>
              </h2>
              <div
                id="collapseOne"
                className="accordion-collapse collapse show"
                aria-labelledby="headingOne"
                data-bs-parent="#paymentAccordion"
              >
                <div className="accordion-body">
                  <form>
                    <div className="mb-3">
                      <label className="form-label">Card Number</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter card number"
                      />
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Expiration Date</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="MM/YY"
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Security Code</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="CVV"
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Cardholder Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Full Name"
                      />
                    </div>
                  </form>
                  <div className="d-flex justify-content-start align-items-center fs-14 gap-3 mt-4 fs-10 ">
                    <input
                      type="checkbox"
                      className="checkBox p-2"
                      id="saveInfo"
                    />
                    <label
                      className="form-check-label text-light-gray"
                      htmlFor="saveInfo"
                    >
                      Save This Info For Future
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div> */}

            
          </div>

          {/* Right Section: Order Summary */}
          <div className="col-12 col-lg-6 col-md-11 col-11 checkout-summary-column">
            <div className="p-5 rounded-1 checkout-summary-card">
              {cartData?.length > 0 ? (
                <div className="cart-container">
                  {cartData.map((product, i) =>
                    product.product_id ? (
                      <div className="cart-item" key={i}>
                        <div className="product-info">
                          {/* {product.custom_product === "yes" ? (
                            <Stage
                              width={100}
                              height={100}
                              className="custom-product-image"
                            >
                              <Layer>
                                <CustomImage
                                  imageUrl={product.image || "/prod.png"}
                                />
                              </Layer>
                            </Stage>
                          ) : ( */}
                            <img loading="lazy" 
                              src={
                                Constants.imgDomain + (product?.customized_image_id?.customized_image || product?.images?.[0] || "/prod.png")
                                
                              }
                              className="product-image"
                              alt="Product"
                            />
                          {/* )} */}
                          {console.log("product", product)}
                          {console.log("product.design_data", product.design_data)}
                          {console.log("product.customized_image_id", product.customized_image_id)}
                          <div className="product-details">
                            <p className="product-name">{product.name}</p>
                            <small className="product-quantity">
                              <b>Price Per Quantity: </b>${product.price}
                            </small>
                            <small className="product-quantity">
                              Quantity: {product.quantity}
                            </small>

                            {product?.price_data && (
                              <div className="matching-bag">
                                <small className="matching-bag-label">
                                  Fabric Price:&nbsp;
                                </small>
                                <div className="h5" style={{ display: "inline" }}>
                                  ${
                                    JSON.parse(product?.price_data)
                                      ?.fabric_price || 0
                                  }
                                </div>
                              </div>
                            )}

                            {product?.price_data && (
                              <div className="matching-bag">
                                <small className="matching-bag-label">
                                  Design Price:&nbsp;
                                </small>
                                <div className="h5" style={{ display: "inline" }}>
                                  ${
                                    JSON.parse(product?.price_data)
                                      ?.design_price || 0
                                  }
                                </div>
                              </div>
                            )}

                            {product?.price_data && (
                              <div className="matching-bag">
                                <small className="matching-bag-label">
                                  Text Price:&nbsp;
                                </small>
                                <div className="h5" style={{ display: "inline" }}>
                                  ${
                                    JSON.parse(product?.price_data)
                                      ?.text_price || 0
                                  }
                                </div>
                              </div>
                            )}

                            {(() => {
                              try {
                                // Check multiple possible locations for design_data
                                let designData = null;
                                
                                // First check product.design_data
                                if (product?.design_data) {
                                  designData = typeof product.design_data === 'string' 
                                    ? JSON.parse(product.design_data) 
                                    : product.design_data;
                                }
                                
                                // If not found, check customized_image_id.design_data
                                if (!designData && product?.customized_image_id?.design_data) {
                                  designData = typeof product.customized_image_id.design_data === 'string'
                                    ? JSON.parse(product.customized_image_id.design_data)
                                    : product.customized_image_id.design_data;
                                }
                                
                                // If not found, check if customized_image_id itself has addOns
                                if (!designData && product?.customized_image_id) {
                                  designData = product.customized_image_id;
                                }
                                
                                console.log("Parsed designData for add-ons:", designData);
                                
                                if (designData?.addOns && Array.isArray(designData.addOns) && designData.addOns.length > 0) {
                                  const totalAddOnsPrice = designData.addOns.reduce((sum, addon) => sum + (addon.price || 0), 0);
                                  
                                  return (
                                    <div className="matching-bag mt-2">
                                      <div className="mb-2">
                                        <small className="matching-bag-label fw-bold">
                                          Add-ons:
                                        </small>
                                      </div>
                                      <ul className="list-unstyled ms-3 mb-2">
                                        {designData.addOns.map((addon, index) => (
                                          <li key={index} className="mb-1">
                                            <small>
                                              • {addon.name || addon.title}
                                              {addon.quantity > 1 && ` (x${addon.quantity})`}
                                              {addon.price && (
                                                <span className="text-muted">
                                                  {' - $'}
                                                  {addon.price.toFixed(2)}
                                                  {addon.quantity > 1 && ' each'}
                                                </span>
                                              )}
                                            </small>
                                          </li>
                                        ))}
                                      </ul>
                                      <div className="h5">
                                        <span className="fw-bold">Total Add-ons Price: </span>
                                        ${totalAddOnsPrice.toFixed(2)}
                                      </div>
                                    </div>
                                  );
                                }
                              } catch (e) {
                                console.error('Error parsing add-ons data:', e);
                                return null;
                              }
                            })()}

                            <div className="mt-2">
                              <small className="matching-bag-label fw-bold">
                                Shipping Charges:&nbsp;
                              </small>
                              <div className="h5" style={{ display: "inline" }}>
                                ${Number(product?.shipping_charges ?? product?.shippingCharges ?? (shipping_charges || 0)).toFixed(2)}
                              </div>
                            </div>
                            {product?.matching_bag && (
                              <div className="matching-bag">
                                <small className="matching-bag-label">
                                  Matching Bag&nbsp;
                                </small>
                                <small className="matching-bag-name">
                                  {JSON.parse(product.matching_bag)?.name}
                                </small>

                                <br />
                                <div className="h5">
                                  <span className="fw-bold">
                                    {" "}
                                    Matching Bag Price:{" "}
                                  </span>
                                  ${JSON.parse(product?.matching_bag)?.price || 0}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <p className="product-price">
                          ${product.price * product.quantity}
                        </p>
                      </div>
                    ) : null
                  )}
                </div>
              ) : (
                <Loader />
              )}
            </div>

            <div className="discount-section px-5 checkout-totals">
              {/* <div className="mb-3 d-flex">
              <input
                type="text"
                className="form-control border me-2 fs-18"
                placeholder="Discount code"
              />
              <button className="btn btn-dark">Apply</button>
            </div> */}

              {/* <div className="mb-2 d-flex justify-content-between fs-16">
              <span>Subtotal</span>
              <span>${cartTotal}</span>
            </div> */}
              {/* <div className="mb-2 d-flex justify-content-between fs-16">
              <span>Shipping</span>
              <span>$40.00</span>
            </div> */}

              {cartData.length > 0 ? (
                <>
                  {(() => {
                    const displayTotal = Number((totalAmount + taxAmount).toFixed(2));
                    const displayDueNow =
                      paymentOption === "deposit_50"
                        ? Number((displayTotal * 0.5).toFixed(2))
                        : paymentOption === "pay_later"
                          ? 0
                          : displayTotal;

                    return (
                      <>
                  <div className="d-flex justify-content-between fs-16">
                    <span>Subtotal</span>
                    <span>${totalAmount.toFixed(2)}</span>
                  </div>

                  <div className="d-flex justify-content-between fs-16">
                    <span>Tax</span>
                    <span>${taxAmount.toFixed(2)}</span>
                  </div>
                  <div className="d-flex justify-content-between fs-16">
                    <span>Shipping</span>
                    <span>${displayShippingAmount.toFixed(2)}</span>
                  </div>
                  <div className="d-flex justify-content-between fs-16">
                    <span>UPS Service</span>
                    <span className="checkout-service-value">
                      {formatUpsServiceLabel({
                        serviceCode: selectedShippingServiceCode,
                        serviceDescription: selectedShippingServiceName,
                      })}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between fs-20 total-amount">
                    <strong>Total</strong>
                    <strong>
                      ${Number(displayTotal + displayShippingAmount).toFixed(2)}
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between fs-16">
                    <span>Due Now</span>
                    <span>
                      ${
                        paymentOption === "deposit_50"
                          ? Number(
                              (Number(displayTotal + displayShippingAmount) * 0.5).toFixed(2)
                            )
                          : paymentOption === "pay_later"
                            ? 0
                            : Number(displayTotal + displayShippingAmount)
                      .toFixed(2)}
                    </span>
                  </div>
                      </>
                    );
                  })()}
                </>
              ) : (
                ``
              )}

              <div className="mt-4">
                {renderPaymentSection("mt-4")}
                </div>
            </div>
          </div>
        </div>
      </div >
    </div >
  );
};

export default CheckoutPage;
